import { createElement } from "react";
import { DateTime } from "luxon";
import { AgGrid } from "@jsq/ds/core";
import { ActionButton } from "../../../common/components/ActionButton";
import { actionButtonCellClass } from "../../../common/components/ActionButton/styles_vanilla.css";
import Money from "../../Money";
import { CombinedStatusRenderer } from "../../shared/CombinedStatusRenderer";
import { PaymentBatchRowItem } from "./types";
import { paymentBatchRequiresReview } from "./utils";

/**
 * List of the supported columns in the Payment Batches table for Distribution Slideout
 */
export enum SupportedColumns {
  CreatedDate = "createdDate",
  Positions = "objectsCount",
  Status = "status",
  Amount = "netAmount",
  Action = "action",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.CreatedDate, "Created date"],
  [SupportedColumns.Positions, "Positions"],
  [SupportedColumns.Status, "Status"],
  [SupportedColumns.Amount, "Amount"],
  [SupportedColumns.Action, "Action"],
]);

/**
 * Determines if a payment batch requires review based on its status.
 * For Canopy, we always enforce 1 FA review when status is READY_FOR_TREASURY_REVIEW.
 * @param batch - The payment batch row item
 * @returns true if the batch requires review, false otherwise
 */
export const requiresReview = (batch: PaymentBatchRowItem): boolean => {
  return paymentBatchRequiresReview(batch.status);
};

export type ActionColumnCallbacks = {
  onReviewClick?: (paymentBatch: PaymentBatchRowItem) => void;
};

/**
 * Base column definitions without the action column
 */
const baseColumnDefinitions: AgGrid.ColDef[] = [
  {
    field: SupportedColumns.CreatedDate,
    headerName: columnNames.get(SupportedColumns.CreatedDate),
    sortable: true,
    valueFormatter: ({ data }: { data: PaymentBatchRowItem }) => {
      if (!data) return "";
      return DateTime.fromISO(data.createdDate).toLocaleString(DateTime.DATE_MED);
    },
    flex: 1,
  },
  {
    field: SupportedColumns.Positions,
    headerName: columnNames.get(SupportedColumns.Positions),
    sortable: true,
    flex: 0.6,
  },
  {
    field: SupportedColumns.Status,
    headerName: columnNames.get(SupportedColumns.Status),
    sortable: true,
    cellRenderer: ({ data }: { data: PaymentBatchRowItem }) => {
      if (!data?.combinedStatusConfig) return null;
      return createElement(CombinedStatusRenderer, {
        config: data.combinedStatusConfig,
      });
    },
    valueFormatter: ({ data }: { data: PaymentBatchRowItem }) => {
      return data?.combinedStatusConfig?.label;
    },
    cellStyle: { alignContent: "center" },
    flex: 1.4,
  },
  {
    field: SupportedColumns.Amount,
    headerName: columnNames.get(SupportedColumns.Amount),
    sortable: true,
    cellRenderer: Money,
    cellRendererParams: ({ data }: { data: PaymentBatchRowItem }) => {
      if (!data) return { money: null, rounded: false };
      return { money: data.netAmount, rounded: false };
    },
    flex: 1,
    cellStyle: { textAlign: "right" },
    headerClass: "ag-right-aligned-header",
  },
];

/**
 * Creates the action column definition with the provided callbacks
 */
const createActionColumnDef = (callbacks: ActionColumnCallbacks): AgGrid.ColDef => ({
  field: SupportedColumns.Action,
  headerName: columnNames.get(SupportedColumns.Action),
  pinned: "right",
  sortable: false,
  filter: false,
  resizable: false,
  flex: 0,
  width: 120,
  cellClass: actionButtonCellClass,
  cellRenderer: ActionButton,
  cellRendererParams: ({ data }: { data: PaymentBatchRowItem | null }) => {
    if (!data) return { action: null };
    return {
      action: data.action,
      onClick: data.action === "review" ? () => callbacks.onReviewClick?.(data) : undefined,
    };
  },
});

/**
 * Creates AG Grid Column definitions for the Payment Batches table
 * @param showActionColumn - Whether to include the action column
 * @param callbacks - Optional callbacks for action button clicks
 */
export const getColumnDefinitions = (
  showActionColumn: boolean = false,
  callbacks: ActionColumnCallbacks = {},
): AgGrid.ColDef[] => {
  if (showActionColumn) {
    return [...baseColumnDefinitions, createActionColumnDef(callbacks)];
  }
  return baseColumnDefinitions;
};

/**
 * @deprecated Use getColumnDefinitions instead for action button click handling
 * AG Grid Column definitions for the Payment Batches table in Distribution Slideout
 */
export const columnDefinitions: AgGrid.ColDef[] = getColumnDefinitions();
